import { r as recognizeNaturalNumbers } from './recognize-natural-numbers.js';
import { t as translateDate } from './translate-date.js';
import './index2.js';

/**
 * **Mazagan Own System (KWS)**
 *
 * Example: <a href="https://www.mazaganbeachresort.com/reservation/rooms/lodging-search?w=0" target="_blank">Mazagan Beach Resort</a>
 *
 * @exports bs_175
 * @category engines
 */
function factory({ window, document, thn }) {
    const engine = {};
    /**
       * @memberOf module:bs_175
       * @see {@link language_check}
       *
       * @param {object} ctx - The context object.
       */
    engine.languageCheck = function languageCheck(ctx) {
        return new Promise(function (resolve, reject) {
            let aux = window.location.pathname.match(ctx.urlRe);
            aux = aux && aux.length && aux[1];
            aux = aux === 'cn' ? 'zh' : aux;
            if (aux && ctx.langs.test(aux)) {
                resolve(aux);
                return;
            }
            resolve('en');
        });
    };
    /**
       * Hijacks <tt>hide_please_wait</tt> function.
       *
       * @memberOf module:bs_175
       * @this thn.widgets.price
       * @see {@link Price#observe_dom}
       *
       * @param {object} ctx - The context object.
       */
    engine.priceObserveDom = function priceObserveDom(ctx) {
        if (!thn._hidePleaseWait && window.hide_please_wait) {
            thn._hidePleaseWait = window.hide_please_wait;
            window.hide_please_wait = function hidePleaseWait() {
                // call original
                thn._hidePleaseWait.apply(this, arguments);
                // THN stuff
                setTimeout(() => ctx.widget.update(), 400);
            };
        }
        const currencyUndelegate = thn.delegate('click', 'input[name=currency]', () => {
            thn.widgets.price.refresh();
        });
        thn.pageUnload(currencyUndelegate);
    };
    /**
       * Implements **priceSpecial**.
       *
       * @memberOf module:bs_175
       * @this thn.widgets.price
       * @see {@link Price#set_data}
       *
       */
    engine._priceParseDataCommon = function _priceParseDataCommon() {
        const widgetData = {};
        const filterInputs = Array.from(document.querySelectorAll('[name="filterBy"] input[type="checkbox"]'));
        // GOOD URL: /palmilla/reservations/best-available-rate/search-results?w=2
        // BAD URL: /palmilla/reservations/palmilla-room-oceanfront/search-results?w=1
        const priceSpecial = (window.location.pathname.match(/reservations/i)
            && !window.location.pathname.match(/best-available-rate/i))
            // See that every input is checked (default), otherwise a filter was applied.
            || (filterInputs
                && filterInputs.length
                && filterInputs.length !== filterInputs.filter((el) => el.checked).length);
        if (priceSpecial) {
            widgetData.priceSpecial = 1;
        }
        return widgetData;
    };
    /**
       * Implements **noRooms** in one case.
       *
       * @memberOf module:bs_175
       * @this thn.widgets.price
       * @see {@link Price#set_data}
       *
       * @param {object} ctx - The context object.
       */
    engine.priceParseData_v1 = function priceParseData_v1(ctx) {
        const widgetData = engine._priceParseDataCommon();
        const $ = window.jQuery;
        let room1Info;
        if (!$) {
            return widgetData;
        }
        widgetData.dateBegin = translateDate(($('.section-travel-info').find('strong').eq(0).text() || '').trim(), thn.data.language);
        widgetData.dateEnd = translateDate(($('.section-travel-info').find('strong').eq(1).text() || '').trim(), thn.data.language);
        widgetData.rooms = 1;
        widgetData.adults = parseInt($('[name=rooms_suites_adults_1]').val(), 10) || 2;
        widgetData.children = parseInt($('[name=rooms_suites_children_1]').val(), 10) || 0;
        widgetData.guests = Math.max(1, widgetData.adults + widgetData.children);
        if (!thn.empty(widgetData.children)) {
            // build the detailed room information
            room1Info = {
                adults: widgetData.adults,
                children: widgetData.children,
                childrenAges: $('[name="rooms_suites_children_1_age[]"]').map(function (idx, el) {
                    return parseInt($(el).val(), 10) || 10;
                }).toArray(),
            };
            // add the room info to the "roomsDispo"
            widgetData.roomsDispo = [room1Info];
        }
        const prices = [];
        widgetData.roomDetails = $('.item[data-product-id]').map(function (idx, el) {
            const $el = $(el);
            const roomData = {
                id: $el.attr('data-product-id'),
                name: $el.find('.description > h3').text().trim(),
                prices: $el.find('.package').map(function (idx2, el2) {
                    const $el2 = $(el2);
                    let price = $el2.find('.total .price').text();
                    if (!widgetData.currency) {
                        widgetData.currency = thn.getCurrencyCode(price.match(/[^\d\s.,]+/)[0]);
                    }
                    price = thn.parseNumber(price);
                    prices.push(price);
                    return {
                        name: $el2.find('.name-column').text().trim(),
                        id: '',
                        rate: '',
                        price: price,
                        breakfast: 0,
                        refundable: 1,
                    };
                }).toArray(),
            };
            const guests = recognizeNaturalNumbers($el.find('.details > p:eq(0)').contents().last().text()
                .trim());
            if (!thn.empty(guests)) {
                roomData.adults = guests[0];
                if (guests.length > 1) {
                    roomData.children = guests[1];
                }
            }
            return roomData;
        }).toArray();
        if ($('.message-container:visible:contains("not available")').length) {
            // NO ROOMS can be detected when clicking on APPLY button and selecting GUESTS
            // otherwise it redirects to Calendar
            widgetData.noRooms = true;
        }
        else if (!thn.empty(prices)) {
            widgetData.price = Math.min(...prices);
            widgetData.set = true;
        }
        return widgetData;
    };
    /**
       * Implements **noRooms** in one case.
       *
       * @memberOf module:bs_175
       * @this thn.widgets.price
       * @see {@link Price#set_data}
       *
       * @param {object} ctx - The context object.
       */
    engine.priceParseData_v2 = function priceParseData_v2(ctx) {
        const widgetData = {};
        const eventData = window.eventData;
        const startDate = ((document.getElementsByName('startDate') || {})[0] || []).value;
        const endDate = ((document.getElementsByName('endDate') || {})[0] || []).value;
        const datesEl = ((document.querySelector('div:nth-child(2) > div:nth-child(1) span') || {}).textContent || '').split(' - ');
        if (eventData) {
            widgetData.dateBegin = (eventData && (eventData.trCi || '').replace(/-/g, '/')) || translateDate((document.querySelector('.BBat:nth-child(1) .BBa2') || {}).textContent || '', thn.data.language);
            widgetData.dateEnd = (eventData && (eventData.trCo || '').replace(/-/g, '/')) || translateDate((document.querySelector('.BBat:nth-child(2) .BBa2') || {}).textContent || '', thn.data.language);
        }
        else if (startDate && endDate) {
            widgetData.dateBegin = thn.dateToString(startDate);
            widgetData.dateEnd = thn.dateToString(endDate);
        }
        else if ((!widgetData.dateBegin || !widgetData.dateEnd) && datesEl.length > 2) {
            widgetData.dateBegin = translateDate(datesEl[0], thn.data.language);
            widgetData.dateEnd = translateDate(datesEl[1], thn.data.language);
        }
        widgetData.rooms = parseInt(eventData && eventData.trRo, 10) || 1;
        widgetData.adults = thn.parseNumber((document.querySelector('.GHo span:nth-child(2)') || {}).textContent) || parseInt(((document.getElementsByName('room0adultCount') || [])[0] || []).value, 10) || 2;
        widgetData.children = thn.parseNumber((document.querySelector('.GHo span:nth-child(3)') || {}).textContent) || parseInt(((document.getElementsByName('room0childCount') || [])[0] || []).value, 10) || 0;
        widgetData.guests = widgetData.adults + widgetData.children;
        const roomsEls = [...document.querySelectorAll('.rooms-list div[class*= labelTotalPriceForStay_overwrite],.rooms-list div[class*=category-title--small]')].filter((room) => thn.isVisible(room));
        const pricesEls = roomsEls.map((room) => {
            const pricesRaw = room.parentElement?.querySelectorAll('div:nth-child(2) span') || [];
            return pricesRaw?.length > 1 ? pricesRaw[1] : pricesRaw[0];
        });
        const prices = [...pricesEls].map((price) => thn.parseNumber(price?.textContent)).filter(Boolean);
        if (prices.length && pricesEls.length) {
            const currency = document.querySelector('input[name=currency]:checked')?.value;
            widgetData.currency = currency || pricesEls[0]?.textContent?.match(/[A-Z]{3}/);
            widgetData.price = Math.min(...prices);
            widgetData.set = true;
        }
        else {
            widgetData.noRooms = true;
        }
        return widgetData;
    };
    /**
       * Implements **bookingId**.
       *
       * @memberOf module:bs_175
       * @this thn.widgets.booking
       * @see {@link Booking#set_data}
       *
       * @param {object} ctx - The context object.
       */
    engine.bookingParseData = function bookingParseData(ctx) {
        const widgetData = {};
        const dataLayer = window.dataLayer;
        const eventData = window.eventData;
        const digitalData = window.digitalData?.transaction;
        // booking ID
        const bookingId = thn.objectGet(dataLayer, 'id', { first: false })
            || document.querySelector('.travel-plan-number .number')?.textContent
            || thn.parseUrlParameter('orderid')
            || thn.dataLayerHelper.get('transactionId')
            || digitalData?.transactionID;
        if (!bookingId) {
            thn.logger.debug('--- Error Getting Booking ID ---');
            return widgetData;
        }
        widgetData.bookingId = bookingId;
        // dates
        const dateBegin = eventData?.tr_ci || thn.objectGet(window.digitalData, 'checkIn');
        const dateEnd = eventData?.tr_co || thn.objectGet(window.digitalData, 'checkOut');
        widgetData.dateBegin = dateBegin?.replace(/-/g, '/');
        widgetData.dateEnd = dateEnd?.replace(/-/g, '/');
        if (!widgetData.dateBegin || !widgetData.dateEnd) {
            const dlCheckIn = thn.dataLayerHelper.get('ecommerce.purchase.products.0.checkInDate');
            const dlCheckOut = thn.dataLayerHelper.get('ecommerce.purchase.products.0.checkoutDate');
            try {
                const dates = document.querySelectorAll('.date .value > strong');
                widgetData.dateBegin = translateDate(dates[0]?.textContent || dlCheckIn, thn.data.language);
                widgetData.dateEnd = translateDate(dates[1]?.textContext || dlCheckOut, thn.data.language);
            }
            catch (e) {
                thn.logger.debug(e, '--- Error Getting Dates ---');
            }
        }
        // price and currency
        const priceEl = document.querySelector('[class^="amount"], .total-price > span');
        const price = priceEl?.getAttribute('amount')?.replace(/['"]+/g, '')
            || priceEl?.textContent
            || thn.dataLayerHelper.get('transactionTotal')
            || thn.objectGet(dataLayer, 'revenue', { first: false })
            || digitalData?.total?.totalPrice;
        const currency = thn.parseCurrency(price)
            || thn.objectGet(dataLayer, 'currency', { first: false })
            || eventData?.tr_cur
            || thn.parseCurrency(digitalData?.total?.currency);
        widgetData.price = thn.parseNumber(price);
        widgetData.currency = currency;
        return widgetData;
    };
    return engine;
}

export { factory };
